import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonText,
  IonDatetime,
  IonIcon,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { GetAppointmentsByDate } from '../data/api';
import { Link, RouteComponentProps } from 'react-router-dom';
import { arrowDownCircle, locationOutline, timeOutline, informationCircleOutline } from 'ionicons/icons';
import '../shared/SchedulingShared.css';

interface StateProps {
  title: string;
}

interface RouteProps extends RouteComponentProps<{ dateValue: string }> {}
interface GenericProps extends RouteProps, StateProps {}

const FingerprintCourt: React.FC<GenericProps> = ({ match }) => {
  if (match.params.dateValue == null) {
    match.params.dateValue = new Date().toISOString();
  }

  const today = new Date(new Date(match.params.dateValue).toDateString());

  const [selectedDate, setSelectedDate] = useState<string>(today.toISOString());

  function GetDatetList(props: { myType: string; myDate: string }) {
    const myQuery = useQuery({
      queryKey: ['GetAppointmentsByDateC', props.myType, props.myDate],
      queryFn: () => GetAppointmentsByDate(props.myType, props.myDate),
    });
    return (
      <div>
        {myQuery.isLoading && <IonItem>Loading</IonItem>}
        {myQuery.isSuccess && myQuery.data.length === 0 && (
          <IonItem lines="none">
            <IonLabel color="medium">No time slots for this date.</IonLabel>
          </IonItem>
        )}
        {myQuery.isSuccess &&
          myQuery.data.map((dateString: any) => (
            <IonItem key={dateString.aptkey}>
              <div
                className="scheduling-slot-indicator"
                data-aptsts={dateString.aptsts}
                slot="start"
              >
                &nbsp;
              </div>
              <div>
                {dateString.aptsts === 'A' ? (
                  <div>
                    <Link
                      to={{
                        pathname:
                          '/tabs/Fingerprintcourtapt/' +
                          dateString.aptkey.toString().replaceAll('/', '|'),
                      }}
                    >
                      <IonLabel>
                        {dateString.aptst}-{dateString.apted}
                      </IonLabel>
                    </Link>
                  </div>
                ) : (
                  <div className="scheduling-slot-label-muted">
                    <IonLabel>
                      {dateString.aptst}-{dateString.apted}
                    </IonLabel>
                  </div>
                )}
              </div>
              <div slot="end" className="scheduling-slot-name-muted">
                <IonLabel>{dateString.aptname}</IonLabel>
              </div>
            </IonItem>
          ))}
      </div>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle color="primary">Court Fingerprint Services</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light" className="passport-page">
        <div className="passport-layout">
              <IonCard className="passport-info-panel">
                <IonCardContent className="passport-info-card">
                  <div className="passport-info-columns">
                    <section className="passport-info-pane">
                      <h2 className="passport-info-pane-title">
                        <IonIcon icon={locationOutline} aria-hidden="true" />
                        Location
                      </h2>
                      <IonText>
                        <p className="passport-info-line">
                          <strong>Orem Justice Court</strong>
                        </p>
                        <p className="passport-info-line">
                          97 East Center Street
                          <br />
                          Orem, UT 84057
                        </p>
                      </IonText>
                    </section>

                    <div className="passport-info-divider" aria-hidden="true" />

                    <section className="passport-info-pane">
                      <h2 className="passport-info-pane-title">
                        <IonIcon icon={timeOutline} aria-hidden="true" />
                        Hours of operation
                      </h2>
                      <IonText className="passport-info-hours">
                        <p>
                          <span className="passport-hours-days">Monday, Tuesday, Wednesday, Thursday:</span>{' '}
                          11:00 AM to 4:00 PM
                        </p>
                        <p>
                          <span className="passport-hours-days">Friday:</span> 8:00 AM to 4:00 PM
                        </p>
                        <p>
                          <span className="passport-hours-days">Saturday &amp; Sunday:</span>{' '}
                          <span className="passport-hours-closed">Closed</span>
                        </p>
                      </IonText>
                    </section>
                  </div>

                  <div className="passport-info-note" role="note">
                    <IonIcon icon={informationCircleOutline} aria-hidden="true" />
                    <span>
                      To schedule an appointment, click a{' '}
                      <strong className="passport-info-note-slot">
                        <span className="passport-info-note-green">green</span> open slot
                      </strong>{' '}
                      below.
                    </span>
                  </div>

                  <div className="passport-info-details">
                    <IonText>
                      <p>
                        To assist you best, please make sure you have selected the correct scheduling service.
                        Fingerprint 311 is for employment purposes and Fingerprint Court is for those with a court
                        case at the Orem Justice Court. Use the Selected Date calendar to change the date, then use
                        the color coding to find an open slot. Once you find a time that works for you, click it and
                        complete the needed information.
                      </p>
                      <p>
                        You will receive a confirmation email that your appointment has been set. If you have any
                        questions contact us at <strong>801-724-3900</strong> (<strong>jcorem@utcourts.gov</strong>).
                      </p>
                    </IonText>
                  </div>
                </IonCardContent>
              </IonCard>

            <div className="passport-schedule-row">
              <IonCard>
                <IonCardContent>
                  <IonItem lines="none">
                    <IonIcon slot="start" icon={arrowDownCircle} color="primary" />
                    <IonLabel>Selected Date</IonLabel>
                  </IonItem>
                  <IonItem mode="ios" lines="none">
                    <IonDatetime
                      value={selectedDate}
                      presentation="date"
                      onIonChange={(e: any) => {
                        setSelectedDate(e.detail.value!);
                      }}
                      placeholder="Select date"
                    />
                  </IonItem>
                </IonCardContent>
              </IonCard>
              <IonCard>
                <IonCardContent>
                  <IonList>
                    <GetDatetList myType="C" myDate={selectedDate} />
                  </IonList>
                </IonCardContent>
              </IonCard>
            </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FingerprintCourt;
